import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getAvatarColor, getInitials } from "../../../utils/avatarUtils";
import "../../common/BadgeMultiSelect/BadgeMultiSelect.scss";
import "./MilestoneRecipientRoles.scss";
import {
  computeAnchoredPopoverPosition,
  getAnchoredPopoverStyle,
  useAnchoredPopoverPosition,
} from "./milestoneAnchoredPopover";

const MAX_VISIBLE_AVATARS = 5;

const INITIAL_POPOVER_POSITION = {
  top: 12,
  bottom: null,
  left: 12,
  openUpward: false,
  listMaxHeight: 220,
  width: 248,
};

const staffIdOf = (staff) =>
  String(staff?.staffId || staff?.StaffId || staff?.value || "").trim();

const staffNameOf = (staff) =>
  String(staff?.staffName || staff?.StaffName || staff?.label || "Unknown").trim();

const staffIsPi = (staff) => Boolean(staff?.isPi || staff?.IsPI || staff?.isPI);

const sortStaffPiFirst = (rows = []) =>
  [...rows].sort(
    (a, b) => Number(staffIsPi(b)) - Number(staffIsPi(a)) || staffNameOf(a).localeCompare(staffNameOf(b)),
  );

const StaffPickerPopover = ({
  open,
  onClose,
  position,
  options,
  selectedStaffIds,
  onSelect,
  popoverRef,
  emptyMessage = "No staff found",
  subtitle = "Select staff for this placeholder",
}) => {
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  const filteredOptions = useMemo(() => {
    const term = search.trim().toLowerCase();
    const base = sortStaffPiFirst(options || []);
    if (!term) return base;
    return base.filter((staff) => {
      const haystack = [
        staffNameOf(staff),
        staff?.roleName,
        staff?.RoleName,
        staffIsPi(staff) ? "pi principal investigator" : "",
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [options, search]);

  if (!open) return null;

  const popover = (
    <div
      className="badge-multi-select__popover badge-multi-select__popover--anchored milestone-recipient-roles__popover"
      ref={popoverRef}
      style={getAnchoredPopoverStyle(position)}
    >
      <div className="badge-multi-select__popover-head">
        <div>
          <strong>Add staff</strong>
          <span>{subtitle}</span>
        </div>
        <button type="button" onClick={onClose} aria-label="Close staff picker">
          ×
        </button>
      </div>
      <div className="milestone-recipient-roles__search-wrap">
        <input
          type="text"
          className="form-control form-control-sm milestone-recipient-roles__search"
          placeholder="Search staff..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
        />
      </div>
      <div
        className="badge-multi-select__popover-list"
        style={{ maxHeight: `${position.listMaxHeight}px` }}
      >
        {filteredOptions.length ? (
          filteredOptions.map((staff) => {
            const staffId = staffIdOf(staff);
            const selected = selectedStaffIds.includes(staffId);
            const isPi = staffIsPi(staff);
            return (
              <button
                key={staffId}
                type="button"
                className={[selected ? "is-selected" : "", isPi ? "is-pi" : ""]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => onSelect(staffId)}
              >
                <span className="milestone-recipient-roles__option-main">
                  <span>{staffNameOf(staff)}</span>
                  {isPi ? (
                    <em className="milestone-recipient-roles__pi-badge" title="Principal Investigator">
                      PI
                    </em>
                  ) : null}
                </span>
                {selected ? <small>Added</small> : null}
              </button>
            );
          })
        ) : (
          <div className="milestone-recipient-roles__empty">{emptyMessage}</div>
        )}
      </div>
    </div>
  );

  return createPortal(popover, document.body);
};

const MilestoneRecipientStaff = ({
  selectedStaffIds = [],
  allStaff = [],
  disabled = false,
  onChange,
  emptyStaffMessage = "No staff available for this study and site.",
  pickerShowsAll = false,
  pickerSubtitle = "Select staff for this placeholder",
}) => {
  const rootRef = useRef(null);
  const addTriggerRef = useRef(null);
  const overflowTriggerRef = useRef(null);
  const addPopoverRef = useRef(null);
  const overflowPopoverRef = useRef(null);

  const [addOpen, setAddOpen] = useState(false);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const [addPosition, setAddPosition] = useState(INITIAL_POPOVER_POSITION);
  const [overflowPosition, setOverflowPosition] = useState(INITIAL_POPOVER_POSITION);

  useAnchoredPopoverPosition(addOpen, addTriggerRef, setAddPosition, { showSearch: true });
  useAnchoredPopoverPosition(overflowOpen, overflowTriggerRef, setOverflowPosition, {
    showSearch: false,
  });

  const staffIds = useMemo(
    () => (Array.isArray(selectedStaffIds) ? selectedStaffIds.map(String) : []),
    [selectedStaffIds],
  );
  const visibleStaffIds = staffIds.slice(0, MAX_VISIBLE_AVATARS);
  const overflowStaffIds = staffIds.slice(MAX_VISIBLE_AVATARS);

  const staffById = useMemo(() => {
    const map = new Map();
    (allStaff || []).forEach((staff) => {
      const id = staffIdOf(staff);
      if (id) map.set(id, staff);
    });
    return map;
  }, [allStaff]);

  const availableStaff = useMemo(
    () => (allStaff || []).filter((staff) => !staffIds.includes(staffIdOf(staff))),
    [allStaff, staffIds],
  );

  const pickerOptions = useMemo(
    () => sortStaffPiFirst(pickerShowsAll ? allStaff || [] : availableStaff),
    [allStaff, availableStaff, pickerShowsAll],
  );

  useEffect(() => {
    if (!addOpen && !overflowOpen) return undefined;

    const handleMouseDown = (event) => {
      const inAddPopover = addPopoverRef.current?.contains(event.target);
      const inOverflowPopover = overflowPopoverRef.current?.contains(event.target);
      const inAddTrigger = addTriggerRef.current?.contains(event.target);
      const inOverflowTrigger = overflowTriggerRef.current?.contains(event.target);

      if (inAddPopover || inOverflowPopover || inAddTrigger || inOverflowTrigger) {
        return;
      }
      setAddOpen(false);
      setOverflowOpen(false);
    };

    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [addOpen, overflowOpen]);

  const addStaff = (staffId) => {
    if (disabled || !onChange || staffIds.includes(staffId)) return;
    onChange([...staffIds, staffId]);
  };

  const removeStaff = (staffId) => {
    if (disabled || !onChange) return;
    onChange(staffIds.filter((id) => id !== staffId));
  };

  const toggleStaff = (staffId) => {
    if (disabled || !onChange) return;
    if (staffIds.includes(staffId)) {
      onChange(staffIds.filter((id) => id !== staffId));
      return;
    }
    onChange([...staffIds, staffId]);
  };

  const openAddPicker = (event) => {
    if (disabled || allStaff.length === 0) return;
    setOverflowOpen(false);
    setAddPosition(computeAnchoredPopoverPosition(event.currentTarget, { showSearch: true }));
    setAddOpen((open) => !open);
  };

  const openOverflowPicker = (event) => {
    if (disabled) return;
    setAddOpen(false);
    setOverflowPosition(computeAnchoredPopoverPosition(event.currentTarget));
    setOverflowOpen((open) => !open);
  };

  if (disabled && staffIds.length === 0) {
    return <span className="text-muted small">—</span>;
  }

  const renderAvatar = (staffId, { onClick, className = "", title } = {}) => {
    const staff = staffById.get(staffId);
    const name = staffNameOf(staff) || staffId;
    const isPi = staffIsPi(staff);
    const style = { backgroundColor: getAvatarColor(name) };
    const classes = [
      "milestone-recipient-roles__avatar",
      isPi ? "milestone-recipient-roles__avatar--pi" : "",
      className,
    ]
      .filter(Boolean)
      .join(" ");
    const avatarTitle = title || (isPi ? `${name} (PI)` : name);

    if (disabled || !onClick) {
      return (
        <span className={classes} style={style} title={avatarTitle}>
          {getInitials(name)}
        </span>
      );
    }

    return (
      <button
        type="button"
        className={classes}
        style={style}
        title={`${avatarTitle} — click to remove`}
        onClick={onClick}
      >
        {getInitials(name)}
      </button>
    );
  };

  const overflowOptions = overflowStaffIds
    .map((staffId) => staffById.get(staffId))
    .filter(Boolean);

  return (
    <div className="milestone-recipient-roles" ref={rootRef}>
      {visibleStaffIds.length > 0 && (
        <div className="milestone-recipient-roles__avatars">
          {visibleStaffIds.map((staffId) => (
            <React.Fragment key={staffId}>
              {renderAvatar(staffId, {
                onClick: disabled ? null : () => removeStaff(staffId),
              })}
            </React.Fragment>
          ))}

          {overflowStaffIds.length > 0 && (
            <>
              {disabled ? (
                <span
                  className="milestone-recipient-roles__avatar milestone-recipient-roles__avatar--more"
                  title={overflowStaffIds
                    .map((staffId) => staffNameOf(staffById.get(staffId)))
                    .join(", ")}
                >
                  +{overflowStaffIds.length}
                </span>
              ) : (
                <button
                  ref={overflowTriggerRef}
                  type="button"
                  className="milestone-recipient-roles__avatar milestone-recipient-roles__avatar--more"
                  title="Show more staff"
                  onClick={openOverflowPicker}
                >
                  +{overflowStaffIds.length}
                </button>
              )}
            </>
          )}
        </div>
      )}

      {!disabled && (
        <button
          ref={addTriggerRef}
          type="button"
          className="badge-multi-select__add milestone-recipient-roles__add"
          disabled={allStaff.length === 0}
          onClick={openAddPicker}
        >
          + Add staff
        </button>
      )}

      <StaffPickerPopover
        open={addOpen}
        onClose={() => setAddOpen(false)}
        position={addPosition}
        options={
          pickerShowsAll
            ? pickerOptions
            : availableStaff.length
              ? pickerOptions
              : sortStaffPiFirst(allStaff || [])
        }
        selectedStaffIds={staffIds}
        onSelect={addStaff}
        popoverRef={addPopoverRef}
        emptyMessage={emptyStaffMessage}
        subtitle={pickerSubtitle}
      />

      <StaffPickerPopover
        open={overflowOpen}
        onClose={() => setOverflowOpen(false)}
        position={overflowPosition}
        options={overflowOptions}
        selectedStaffIds={staffIds}
        onSelect={toggleStaff}
        popoverRef={overflowPopoverRef}
        emptyMessage={emptyStaffMessage}
      />
    </div>
  );
};

export default MilestoneRecipientStaff;
